/**
 * Premium Header — header.js
 * Version: 1.1.0
 * Compatible: Shopify Online Store 2.0
 * Selectors match: header.css (ph-* naming) + premium-header.liquid
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */

  function debounce(fn, delay) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'USD',
      }).format(cents / 100);
    } catch (_) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function esc(str) {
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#039;');
  }

  function highlightMatch(str, query) {
    if (!query) return str;
    return str.replace(
      new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi'),
      '<mark>$1</mark>'
    );
  }

  /* ============================================================
     SVG ICON HELPERS (used in predictive search results)
     ============================================================ */

  const SVG = {
    image: () =>
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
    grid: () =>
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
    article: () =>
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
    arrowRight: () =>
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>',
  };

  /* ============================================================
     STICKY + TRANSPARENT HEADER
     Targets: #premium-header (.ph class)
     CSS classes toggled: is-scrolled, is-hidden
     ============================================================ */

  class StickyHeader {
    constructor(header) {
      this.header   = header;
      this.isSticky = header.classList.contains('ph--sticky');
      this.isTransp = header.classList.contains('ph--transparent');
      this.lastY    = window.scrollY;
      this.ticking  = false;

      if (!this.isSticky && !this.isTransp) return;

      window.addEventListener('scroll', () => this._onScroll(), { passive: true });
      window.addEventListener('resize', debounce(() => this._setVar(), 150), { passive: true });
      this._onScroll();
      this._setVar();
    }

    _setVar() {
      document.documentElement.style.setProperty('--header-height', this.header.offsetHeight + 'px');
    }

    _onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y      = window.scrollY;
        const goDown = y > this.lastY;
        this.header.classList.toggle('is-scrolled', y > 10);
        if (this.isSticky && y > this.header.offsetHeight) {
          this.header.classList.toggle('is-hidden', goDown);
        } else {
          this.header.classList.remove('is-hidden');
        }
        this.lastY   = y;
        this.ticking = false;
      });
    }
  }

  /* ============================================================
     PREDICTIVE SEARCH
     Targets: .ph-search (container)
       .ph-search__input    — text input
       .ph-search__results  — results panel (toggled via hidden attr)
       .ph-search__loading  — spinner row (toggled via hidden attr)
       .ph-search__results-content — injected results HTML
     ============================================================ */

  class PredictiveSearch {
    static attachTo(container) {
      return new PredictiveSearch(container);
    }

    constructor(container) {
      this.container = container;
      this.input     = container.querySelector('.ph-search__input');
      this.results   = container.querySelector('.ph-search__results');
      this.loading   = container.querySelector('.ph-search__loading');
      this.content   = container.querySelector('.ph-search__results-content');

      if (!this.input || !this.results) return;

      this.ctrl      = null;
      this.activeIdx = -1;
      this.items     = [];

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');

      this.input.addEventListener('input', debounce(e => this._onInput(e.target.value.trim()), 280));
      this.input.addEventListener('keydown', e => this._onKey(e));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2 && !this.results.hidden) this._open();
      });
      this.input.addEventListener('blur', debounce(() => this._close(), 200));
      document.addEventListener('click', e => {
        if (!this.container.contains(e.target)) this._close();
      });
    }

    async _onInput(q) {
      if (q.length < 2) { this._close(); return; }
      this._open();
      if (this.loading) this.loading.hidden = false;
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();

      const root   = (typeof Shopify !== 'undefined' && Shopify.routes?.root) || '/';
      const params = new URLSearchParams({
        q,
        'resources[type]':  'product,collection,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });

      try {
        const res  = await fetch(root + 'search/suggest.json?' + params, { signal: this.ctrl.signal });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        this._render(data?.resources?.results || {}, q);
      } catch (err) {
        if (err.name !== 'AbortError' && this.content) {
          this.content.innerHTML = '<p class="ph-search__empty">Something went wrong. Please try again.</p>';
        }
      } finally {
        if (this.loading) this.loading.hidden = true;
      }
    }

    _render(results, q) {
      if (!this.content) return;
      const { products = [], collections = [], articles = [] } = results;

      if (!products.length && !collections.length && !articles.length) {
        this.content.innerHTML = '<p class="ph-search__empty">No results for "<strong>' + esc(q) + '</strong>"</p>';
        this.items = [];
        return;
      }

      let html = '';

      if (products.length) {
        html += '<div class="ph-search__group"><span class="ph-search__group-label">Products</span>';
        products.forEach(p => {
          const img = p.image
            ? '<img src="' + esc(p.image) + '" alt="" class="ph-search__result-img" loading="lazy" width="44" height="44">'
            : '<div class="ph-search__result-img-placeholder">' + SVG.image() + '</div>';
          html +=
            '<a class="ph-search__result-item" href="' + esc(p.url) + '" role="option">' +
            img +
            '<span class="ph-search__result-info">' +
            '<span class="ph-search__result-title">' + highlightMatch(esc(p.title), esc(q)) + '</span>' +
            (p.vendor ? '<span class="ph-search__result-vendor">' + esc(p.vendor) + '</span>' : '') +
            (p.price != null ? '<span class="ph-search__result-price">' + formatMoney(p.price) + '</span>' : '') +
            '</span></a>';
        });
        html += '</div>';
      }

      if (collections.length) {
        html += '<div class="ph-search__group"><span class="ph-search__group-label">Collections</span>';
        collections.forEach(c => {
          html +=
            '<a class="ph-search__result-item" href="' + esc(c.url) + '" role="option">' +
            '<div class="ph-search__result-img-placeholder">' + SVG.grid() + '</div>' +
            '<span class="ph-search__result-info">' +
            '<span class="ph-search__result-title">' + highlightMatch(esc(c.title), esc(q)) + '</span>' +
            '</span></a>';
        });
        html += '</div>';
      }

      if (articles.length) {
        html += '<div class="ph-search__group"><span class="ph-search__group-label">Articles</span>';
        articles.forEach(a => {
          html +=
            '<a class="ph-search__result-item" href="' + esc(a.url) + '" role="option">' +
            '<div class="ph-search__result-img-placeholder">' + SVG.article() + '</div>' +
            '<span class="ph-search__result-info">' +
            '<span class="ph-search__result-title">' + highlightMatch(esc(a.title), esc(q)) + '</span>' +
            '</span></a>';
        });
        html += '</div>';
      }

      html +=
        '<a class="ph-search__view-all" href="/search?type=product,article,collection&q=' +
        encodeURIComponent(q) + '">View all results for &ldquo;' + esc(q) + '&rdquo; ' +
        SVG.arrowRight() + '</a>';

      this.content.innerHTML = html;
      this.items = [...this.content.querySelectorAll('[role="option"]')];
      this.activeIdx = -1;
    }

    _onKey(e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this._setActive(this.activeIdx + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this._setActive(this.activeIdx - 1);
          break;
        case 'Enter':
          if (this.activeIdx >= 0) { e.preventDefault(); this.items[this.activeIdx]?.click(); }
          break;
        case 'Escape':
          this._close();
          this.input.blur();
          break;
      }
    }

    _setActive(idx) {
      this.items.forEach(el => el.setAttribute('aria-selected', 'false'));
      this.activeIdx = Math.max(-1, Math.min(idx, this.items.length - 1));
      if (this.activeIdx >= 0) {
        this.items[this.activeIdx].setAttribute('aria-selected', 'true');
        this.items[this.activeIdx].scrollIntoView({ block: 'nearest' });
      }
    }

    _open()  { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    _close() { this.results.hidden = true;  this.input.setAttribute('aria-expanded', 'false'); this.items = []; this.activeIdx = -1; }
  }

  /* ============================================================
     MOBILE DRAWER
     Targets: #ph-mobile-drawer  — dialog element
              #ph-mobile-toggle  — hamburger button in header
              #ph-drawer-close   — close button inside panel
              #ph-drawer-overlay — click-away overlay
              .ph-drawer__expand — accordion expand buttons
              .ph-drawer__item   — nav list items
              .ph-drawer__children — submenu list (hidden attr)
     ============================================================ */

  class MobileDrawer {
    constructor() {
      this.drawer   = document.getElementById('ph-mobile-drawer');
      this.toggle   = document.getElementById('ph-mobile-toggle');
      this.closeBtn = document.getElementById('ph-drawer-close');
      this.overlay  = document.getElementById('ph-drawer-overlay');
      this.panel    = this.drawer?.querySelector('.ph-drawer__panel');
      this._isOpen  = false;

      if (!this.drawer || !this.toggle) return;

      this.toggle.addEventListener('click',  () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click',  () => this.close());
      this.drawer.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

      this._initAccordion();
      this._initSwipe();
    }

    open() {
      this._isOpen = true;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      this.toggle.setAttribute('aria-expanded', 'true');

      const ham   = this.toggle.querySelector('.ph-icon__svg--ham');
      const close = this.toggle.querySelector('.ph-icon__svg--close');
      if (ham)   ham.hidden   = true;
      if (close) close.hidden = false;

      requestAnimationFrame(() => {
        const first = this.panel?.querySelector('a[href], button:not([disabled]), input');
        first?.focus();
      });
    }

    close() {
      this._isOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.toggle.setAttribute('aria-expanded', 'false');

      const ham   = this.toggle.querySelector('.ph-icon__svg--ham');
      const close = this.toggle.querySelector('.ph-icon__svg--close');
      if (ham)   ham.hidden   = false;
      if (close) close.hidden = true;

      this.toggle.focus();
      if (this.panel) {
        this.panel.addEventListener('transitionend', () => {
          if (!this._isOpen) this.drawer.hidden = true;
        }, { once: true });
      }
    }

    _initAccordion() {
      this.drawer?.querySelectorAll('.ph-drawer__expand').forEach(btn => {
        btn.addEventListener('click', () => {
          const isExp    = btn.getAttribute('aria-expanded') === 'true';
          const children = btn.closest('.ph-drawer__item')?.querySelector('.ph-drawer__children');
          btn.setAttribute('aria-expanded', String(!isExp));
          if (children) {
            children.hidden = isExp;
            if (!isExp) {
              children.style.maxHeight = children.scrollHeight + 'px';
            } else {
              children.style.maxHeight = '0';
            }
          }
        });
      });
    }

    _initSwipe() {
      if (!this.panel) return;
      let startX = 0, startY = 0, active = false;
      this.panel.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        active = true;
      }, { passive: true });
      this.panel.addEventListener('touchmove', e => {
        if (!active) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        if (dx < -20 && Math.abs(dy) < 60) {
          this.panel.style.transition = 'none';
          this.panel.style.transform  = 'translateX(' + dx + 'px)';
        }
      }, { passive: true });
      this.panel.addEventListener('touchend', e => {
        if (!active) return;
        active = false;
        const dx = e.changedTouches[0].clientX - startX;
        this.panel.style.transition = '';
        this.panel.style.transform  = '';
        if (dx < -80) this.close();
      }, { passive: true });
    }
  }

  /* ============================================================
     CART COUNT
     Targets: #ph-cart-count — badge span
              #ph-cart-icon  — cart link element
     Updates via theme events + initial page-load fetch.
     ============================================================ */

  class CartCount {
    constructor() {
      this.badge = document.getElementById('ph-cart-count');
      this.icon  = document.getElementById('ph-cart-icon');
      if (!this.badge) return;

      /* Listen for cart events fired by the theme after add/update */
      document.addEventListener('cart:updated',      e => this._set(e.detail?.cart?.item_count ?? e.detail?.itemCount));
      document.addEventListener('theme:cart:change', e => this._set(e.detail?.item_count));

      /* Fetch the current count once on page load */
      this._fetchAndSet();
    }

    _set(n) {
      if (n == null) return;
      const count = parseInt(n, 10) || 0;
      this.badge.textContent = count > 99 ? '99+' : count;
      this.badge.classList.toggle('is-empty', count === 0);

      if (this.icon) {
        this.icon.setAttribute('aria-label', 'Shopping cart, ' + count + ' ' + (count === 1 ? 'item' : 'items'));
      }

      if (count > 0) {
        this.badge.classList.remove('is-bumping');
        void this.badge.offsetWidth;
        this.badge.classList.add('is-bumping');
        this.badge.addEventListener('animationend', () => this.badge.classList.remove('is-bumping'), { once: true });
      }
    }

    async _fetchAndSet() {
      try {
        const res  = await fetch('/cart.js');
        const cart = await res.json();
        this._set(cart.item_count);
      } catch (_) {}
    }
  }

  /* ============================================================
     MEGA MENU KEYBOARD ACCESS
     Targets: .ph-nav__item[data-nav-item] — nav list items
              .ph-nav__link                — top-level link
              .ph-dropdown, .mega-menu     — dropdown panels
     ============================================================ */

  class MegaMenuAccess {
    constructor() {
      this.items    = document.querySelectorAll('.ph-nav__item[data-nav-item]');
      this.openItem = null;
      if (!this.items.length) return;
      this._init();
    }

    _clampMenu(menu) {
      if (!menu.classList.contains('mega-menu')) return;
      menu.style.left = '';

      requestAnimationFrame(() => {
        const rect   = menu.getBoundingClientRect();
        const vpW    = window.innerWidth;
        const MARGIN = 12;

        if (rect.left >= MARGIN && rect.right <= vpW - MARGIN) return;

        const parentLeft = (menu.offsetParent || document.body).getBoundingClientRect().left;
        const halfW      = menu.offsetWidth / 2;

        if (rect.left < MARGIN) {
          menu.style.left = `${MARGIN - parentLeft + halfW}px`;
        } else {
          menu.style.left = `${vpW - MARGIN - parentLeft - halfW}px`;
        }
      });
    }

    _init() {
      this.items.forEach(item => {
        const link = item.querySelector(':scope > .ph-nav__link');
        const menu = item.querySelector(':scope > .ph-dropdown, :scope > .mega-menu');
        if (!link || !menu) return;

        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');

        item.addEventListener('mouseenter', () => {
          link.setAttribute('aria-expanded', 'true');
          item.classList.add('is-open');
          this._clampMenu(menu);
        });
        item.addEventListener('mouseleave', () => {
          link.setAttribute('aria-expanded', 'false');
          item.classList.remove('is-open');
        });

        link.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (this.openItem && this.openItem !== item) this._closeItem(this.openItem);
            const willOpen = !item.classList.contains('is-open');
            item.classList.toggle('is-open', willOpen);
            link.setAttribute('aria-expanded', String(willOpen));
            if (willOpen) {
              this.openItem = item;
              this._clampMenu(menu);
              menu.querySelector('a')?.focus();
            } else {
              this.openItem = null;
            }
          }
          if (e.key === 'Escape') { this._closeItem(item); link.focus(); }
        });

        menu.addEventListener('keydown', e => {
          if (e.key === 'Escape') { this._closeItem(item); link.focus(); }
        });
      });

      document.addEventListener('click', e => {
        if (!e.target.closest('.ph-nav')) {
          this.items.forEach(i => this._closeItem(i));
        }
      });
    }

    _closeItem(item) {
      item.classList.remove('is-open');
      item.querySelector(':scope > .ph-nav__link')?.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
    }
  }

  /* ============================================================
     INITIALISE
     ============================================================ */

  function init() {
    const header = document.getElementById('premium-header');
    if (!header) return;

    new StickyHeader(header);
    new MobileDrawer();
    new CartCount();
    new MegaMenuAccess();

    /* Desktop pill search */
    const desktopSearch = header.querySelector('.ph-col--search .ph-search');
    if (desktopSearch) PredictiveSearch.attachTo(desktopSearch);
  }



  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Public API for theme integrations */
  window.PremiumHeader = { CartCount, PredictiveSearch };

})();